import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { authClient } from "@/auth/auth-client";
// oxlint-disable-next-line import/no-unassigned-import -- CSS side-effect import is the canonical pattern for scoped styles
import "@/speech-agent/speech-agent.css";

const MOBILE_MODE_KEY = "speech-agent.mobileMode";
const THEME_KEY = "speech-agent.theme";

type Theme = "auto" | "light" | "dark";

export const Route = createFileRoute("/app/speech-agent")({
  loader: ({ context }) => {
    if (!context.session?.user) {
      throw redirect({ to: "/sign-in" });
    }
    return { userEmail: context.session.user.email };
  },
  component: SpeechAgentLayout,
});

function SpeechAgentLayout() {
  const { userEmail } = Route.useLoaderData();
  const router = useRouter();
  const [mobileMode, setMobileMode] = useState(false);
  const [theme, setTheme] = useState<Theme>("auto");

  // Apply theme + mobile-mode classes to <body>. Cleaned up on unmount so
  // the styles don't leak to other template pages.
  useEffect(() => {
    document.body.classList.add("in-speech-agent");
    return () => {
      document.body.classList.remove(
        "in-speech-agent",
        "theme-light",
        "theme-dark",
        "mobile-mode"
      );
    };
  }, []);

  useEffect(() => {
    try {
      setMobileMode(
        localStorage.getItem(MOBILE_MODE_KEY) === "1"
      );
      const stored = localStorage.getItem(
        THEME_KEY
      ) as Theme | null;
      if (
        stored === "light" ||
        stored === "dark" ||
        stored === "auto"
      ) {
        setTheme(stored);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    document.body.classList.toggle(
      "mobile-mode",
      mobileMode
    );
    try {
      localStorage.setItem(
        MOBILE_MODE_KEY,
        mobileMode ? "1" : "0"
      );
    } catch {
      // ignore
    }
  }, [mobileMode]);

  useEffect(() => {
    document.body.classList.remove(
      "theme-light",
      "theme-dark"
    );
    if (theme === "light")
      document.body.classList.add("theme-light");
    if (theme === "dark")
      document.body.classList.add("theme-dark");
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const cycleTheme = () => {
    setTheme((t) =>
      t === "auto"
        ? "light"
        : t === "light"
          ? "dark"
          : "auto"
    );
  };

  const handleLogout = async () => {
    await authClient.signOut();
    void router.navigate({ to: "/sign-in" });
  };

  return (
    <>
      <div className="canvas" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />
      <div className="app-shell">
        <header className="site-header">
          <Link to="/app/speech-agent" className="brand">
            <span className="brand-mark" aria-hidden="true">
              <span className="brand-mark-dot" />
            </span>
            <span className="brand-name">
              Speech
              <span className="brand-name-thin">
                {" "}
                Agent
              </span>
            </span>
          </Link>
          <nav className="site-nav">
            <Link
              to="/app/speech-agent"
              data-route="home"
              activeProps={{ className: "active" }}
              activeOptions={{ exact: true }}
            >
              Konverzace
            </Link>
            <Link
              to="/app/speech-agent/historie"
              data-route="historie"
              activeProps={{ className: "active" }}
            >
              Historie
            </Link>
            <Link
              to="/app/speech-agent/agents"
              data-route="agents"
              activeProps={{ className: "active" }}
            >
              Agenti
            </Link>
            <span className="nav-user">
              <span
                className="nav-user-email"
                title={userEmail}
              >
                {userEmail}
              </span>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => void handleLogout()}
              >
                Odhlásit
              </button>
            </span>
            <button
              type="button"
              className="nav-icon-btn"
              title="Přepnout vzhled (auto / světlý / tmavý)"
              aria-label="Přepnout vzhled"
              onClick={cycleTheme}
            >
              <svg
                className="nav-toggle-icon theme-icon-sun"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="4" />
                <line x1="12" y1="2" x2="12" y2="5" />
                <line x1="12" y1="19" x2="12" y2="22" />
                <line x1="2" y1="12" x2="5" y2="12" />
                <line x1="19" y1="12" x2="22" y2="12" />
                <line
                  x1="4.93"
                  y1="4.93"
                  x2="7.05"
                  y2="7.05"
                />
                <line
                  x1="16.95"
                  y1="16.95"
                  x2="19.07"
                  y2="19.07"
                />
                <line
                  x1="4.93"
                  y1="19.07"
                  x2="7.05"
                  y2="16.95"
                />
                <line
                  x1="16.95"
                  y1="7.05"
                  x2="19.07"
                  y2="4.93"
                />
              </svg>
              <svg
                className="nav-toggle-icon theme-icon-moon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
              </svg>
              <svg
                className="nav-toggle-icon theme-icon-auto"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 3v18" />
                <path
                  d="M12 3a9 9 0 0 1 0 18z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button
              type="button"
              className="nav-toggle"
              aria-pressed={mobileMode}
              title="Náhled jako na mobilu"
              onClick={() => setMobileMode((m) => !m)}
            >
              <svg
                className="nav-toggle-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <rect
                  x="6"
                  y="3"
                  width="12"
                  height="18"
                  rx="2.5"
                />
                <line x1="11" y1="18" x2="13" y2="18" />
              </svg>
              <span className="nav-toggle-label">
                {mobileMode ? "Desktop" : "Mobil"}
              </span>
            </button>
          </nav>
        </header>

        <main>
          <Outlet />
        </main>

        <footer className="site-footer">
          <span className="footer-mark">⌖</span>
          <span className="footer-text">
            Speech Agent · PoC
          </span>
        </footer>

        <nav
          className="mobile-tabs"
          aria-label="Hlavní navigace"
        >
          <Link
            to="/app/speech-agent"
            activeProps={{ className: "active" }}
            activeOptions={{ exact: true }}
          >
            <svg
              className="tab-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <rect
                x="9"
                y="3"
                width="6"
                height="11"
                rx="3"
              />
              <path d="M5 11v1a7 7 0 0 0 14 0v-1" />
              <line x1="12" y1="19" x2="12" y2="22" />
            </svg>
            <span>Konverzace</span>
          </Link>
          <Link
            to="/app/speech-agent/historie"
            activeProps={{ className: "active" }}
          >
            <svg
              className="tab-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15 14" />
            </svg>
            <span>Historie</span>
          </Link>
          <Link
            to="/app/speech-agent/agents"
            activeProps={{ className: "active" }}
          >
            <svg
              className="tab-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
              <path d="M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
              <circle cx="12" cy="12" r="3.5" />
            </svg>
            <span>Agenti</span>
          </Link>
        </nav>
      </div>
    </>
  );
}
